import _ from 'underscore';

import suggestEvents from '../events/suggest';
import BaseView from '../base-view';
import i18n from '../utils/i18n';
import Suggest from '../components/suggest/index';
import PriceField from '../components/field-price/index';
import DecimalField from '../components/field-decimal/index';
import FormValidationView from '../components/form-validation/view';
import CurrencyView from '../components/field-currency/view';
import DateView from '../components/field-date/view';
import ToggleView from '../components/toggle/view';

const REQUIRED_FIELD_CLASS = 'field--required';
const IS_HIDDEN_CLASS = 'is-hidden';

export default BaseView.extend({
	events: {
		'input .js-amount': 'onAmountInput',
		'input .js-price': 'onPriceInput',
		'click .js-submit': 'onFormSubmit',
		'blur .js-exchange': 'setTradeMatchIdRequiredState',
		'change .js-contra-firm-select': 'onContraFirmChange',
		'input .js-cfi-code-input': 'onCfiInput',
		'input .js-instrument-input': 'onInstrumentInput',
	},

	initialize: function() {
		this.formHasValidated = false;

		// reference elements
		this.settlementAmountLabel = this.query(this.el, '.js-settlement-amount-label');
		this.settlementAmountBondsLabel = this.query(this.el, '.js-settlement-amount-bonds-label');
		this.tradeMatchIdField = this.query(this.el, '.js-trade-match-id-field');
		this.tradeMatchIdInput = this.query(this.el, '.js-trade-match-id-input');
		this.instrumentSection = this.query(this.el, '.js-instrument');
		this.instrumentInput = this.query(this.el, '.js-instrument-input');
		this.instrumentAutocompleteInput = this.query(this.el, '.js-instrument-known-isin');
		this.cfiPartyIdSearchInput = this.query(this.el, '.js-contra-firm-party-id-search');
		this.cfiCodeSection = this.query(this.el, '.js-cfi-code');
		this.cfiCodeInput = this.query(this.el, '.js-cfi-code-input');
		this.cfiDefinition = JSON.parse(atob(this.el.dataset.cfiDefinition));
		this.underlyingsSection = this.query(this.el, '.js-underlyings');
		this.contraFirmSelect = this.query(this.el, '.js-contra-firm-select');
		this.beneficialOwnerField = this.query(this.el, '.js-beneficial-owner-field');
		this.beneficialOwnerInput = this.query(this.el, '.js-beneficial-owner-input');

		// create subviews
		this.subviews = {
			reportForm: new FormValidationView({
				el: '.js-form'
			}),

			suggestInstrumentView: new Suggest.Views.Main({
				el: '.js-search-instrument',
				dataUrl: '/autocomplete'
			}),

			underlyingOneView: new Suggest.Views.Main({
				el: '.js-underlying-input-1',
				dataUrl: '/autocomplete'
			}),

			underlyingTwoView: new Suggest.Views.Main({
				el: '.js-underlying-input-2',
				dataUrl: '/autocomplete'
			}),

			underlyingThreeView: new Suggest.Views.Main({
				el: '.js-underlying-input-3',
				dataUrl: '/autocomplete'
			}),

			currencyView: new CurrencyView({
				el: '.js-search-currency'
			}),

			priceView: new PriceField.Views.Main({
				el: '.js-price'
			}),

			chfAmountView: new DecimalField.Views.Main({
				el: '.js-chf-amount'
			}),

			strikePriceView: new DecimalField.Views.Main({
				el: '.js-strike-price'
			}),

			leverageIndicatorView: new DecimalField.Views.Main({
				el: '.js-leverage-indicator'
			}),

			dateExecutionView: new DateView({
				el: '.js-date-execution'
			}),

			dateValueView: new DateView({
				el: '.js-date-value'
			})
		};

		this.parsleyForm = this.subviews.reportForm.parsleyForm;

		// Check if condional suggest view has to be created
		if (this.contraFirmSelect.value.toLowerCase() === 'memb') {
			this.addContraFirmIdSuggestView(false);
		}

		// create auto-next views (inputs)
		this.createLeadingZeroFields();

		// create togglable elements
		this.createToggleElements();

		// listeners
		this.listenTo(this.subviews.currencyView.model, 'change:Currency', this.onCurrencyChange);
		this.listenTo(this.subviews.dateExecutionView.model, 'change:Date', this.onExecutionDateChange);
		this.listenTo(this.subviews.suggestInstrumentView, suggestEvents.SELECTED, this.validateInstrumentField);

		this.onCurrencyChange(null, this.subviews.currencyView.model.get('Currency'));

		this.setCfiAndIsinValidation();

		// check success notification
		this.removeNotification();

		// set field requirements based on current input value
		this.toggleCfiRequired();
		this.toggleInstrumentRequired();
		this.setContraFirmValidationRules();
	},

	/**
	 * Adds validation to ISIN field
	 * if CFI is filled in, ISIN is not required
	 * if CFI is empty, ISIN should be a value from the dropdown
	 * if CFI is filled, ISIN can be a custom value (not from dropdown)
	 */
	setCfiAndIsinValidation: function() {
		const { feedbackKnownIsin: errorMessage } = this.instrumentInput.dataset;

		window.Parsley.addValidator('conditionalIsin', {
			validateString: (val) => {
				// ISIN not required when CFI code is filled in
				if (this.cfiCodeInput.value) {
					return true;
				}

				// Known ISIN (selected from dropdown) required when no CFI code is filled in
				if (!this.instrumentAutocompleteInput.value) {
					return false;
				}

				return true;
			},

			messages: {
				en: errorMessage,
			}
		});

	},
	/**
	 * Add event listener to fields which needs a leading zero
	 **/
	createLeadingZeroFields: function() {
		var fields = this.queryAll('input[data-leading-zero]');

		if (!fields.length) {
			return;
		}

		fields.forEach(function(field, index) {
			field.addEventListener('blur', this.addLeadingZero);
		}, this);
	},

	/**
	 * Add a leading zero to a specific field
	 **/
	addLeadingZero: function(e) {
		var el = e.currentTarget,
			val = el.value;

		if (val.length === 1) {
			el.value = 0 + val;
		}
	},

	/**
	 * create togglable fields (remarks, email)
	 **/
	createToggleElements: function() {
		var toggleTriggers = this.queryAll('.js-toggle-element');

		if (!toggleTriggers.length) {
			return;
		}

		toggleTriggers.forEach(function(triggerEl) {
			return new ToggleView({ el: triggerEl });
		}, this);

	},

	/**
	 * Validates the instrument field
	 * @returns {Boolean}
	 */
	validateInstrumentField() {
		this.parsleyForm.validate({group: 'instrument'});
	},

	/**
	 * Validates the parsley form
	 * @returns {Boolean}
	 */
	validateForm() {
		this.parsleyForm.validate();

		this.formHasValidated = true;

		return this.parsleyForm.validationResult;
	},

	/**
	 * form submit handler
	 * determine the action of the form depending on the clicked submit button
	 * could be report, or add/save to queue
	 **/
	onFormSubmit: function(e) {
		const { currentTarget: submitButton } = e;
		const { form } = submitButton;
		const formAction = submitButton.getAttribute('data-action') || '/extranet/tradereporting';

		e.preventDefault();

		const formIsValid = this.validateForm();

		if (formIsValid) {
			// set action of the forms
			form.setAttribute('action', formAction);

			// disable all submit buttons to prevent multiple submissions
			this.queryAll('[type=submit]').forEach(function(submitButton) {
				submitButton.setAttribute('disabled', 'disabled');
			});

			form.submit();

			return false;
		}
	},

	onAmountInput: function() {
		this.updateSettlementAmount();
	},

	onPriceInput: function() {
		this.updateSettlementAmount();
	},

	onCfiInput: function(e) {
		const { target: { value } } = e;

		this.setUnderlyingVisibility(value);
		this.updateCfiRequiredFields(value);

		this.toggleInstrumentRequired();
	},

	onInstrumentInput(e) {
		this.toggleCfiRequired();
	},

	/**
	 * Toggle instrument required based on CFI input value
	 */
	toggleInstrumentRequired() {
		const { value } = this.cfiCodeInput;

		if (value) {
			this.removeFieldRequired('instrumentSuggest');
		} else {
			this.setFieldRequired('instrumentSuggest');
		}
	},

	/**
	 * Toggle CFI required based on instrument input value
	 */
	toggleCfiRequired() {
		const { value } = this.instrumentInput;

		value
			? this.removeFieldRequired('cfiCode')
			: this.setFieldRequired('cfiCode');
	},

	/**
	 * Set values to specific fields based on the the report type
	 * See Jira issues BX-652, BX-653
	 **/
	setTradeReportTypeInputs: function(reportType) {
		var inputCustomer = this.query('.js-customer');

		if (reportType === 'onesided') {
			inputCustomer.classList.remove(IS_HIDDEN_CLASS);
		} else if (reportType === 'twosided') {
			inputCustomer.classList.add(IS_HIDDEN_CLASS);
			inputCustomer.checked = false;
			inputCustomer.removeAttribute('checked');
		}
	},

	/**
	 * execution date change handler
	 * does an ajax request to get next valid value date
	 * after request, update value date view
	 */
	onExecutionDateChange: function(model, executionDate) {
		// get the date and format it for the backend
		var dateValueView = this.subviews.dateValueView,
			dateExecutionView = this.subviews.dateExecutionView,
			dateFormatted = executionDate.getFullYear() + '-' + (executionDate.getMonth() + 1) + '-' + executionDate.getDate(),
			date;

		dateExecutionView.disable();

		$.ajax({
			url: '/extranet/tradereporting/valuedate',
			data: {executiondate: dateFormatted}
		}).then(function(timestamp) {
			date = new Date(parseInt(timestamp));
			dateValueView.setDate(date);
		}, function() {
			console.warn('error in getting valuedate');
		}).always(function() {
			dateExecutionView.enable();
		});
	},

	/**
	 * currency change handler
	 **/
	onCurrencyChange: function(model, currency) {
		// pass the selected currency to the price field views
		this.subviews.priceView.model.set('currency', currency);

		// update settlement amount
		this.updateSettlementAmount();
	},

	/**
	 * Update the settlement amount in the view
	 **/
	updateSettlementAmount: function() {
		var amount = this.query(this.el, '.js-amount').value || 0,
			price = this.subviews.priceView.model.get('price') || 0,
			currency = this.subviews.priceView.model.get('currency') || 'CHF',
			settlementAmount = parseFloat(amount) * parseFloat(price),
			settlementAmountBonds = settlementAmount / 100;

		this.settlementAmountLabel.textContent = i18n.formatNumberAsCurrency(settlementAmount, currency, 4);
		this.settlementAmountBondsLabel.textContent = i18n.formatNumberAsCurrency(settlementAmountBonds, currency, 4);
	},

	/**
	 * Remove notification if there is one
	 **/
	removeNotification: function() {
		var el = this.query(this.el, '.js-report-notification');

		if (!el) {
			return;
		}

		// remove notification after timeout
		setTimeout(function() {
			el.classList.add(IS_HIDDEN_CLASS);
		}, 3000);
	},

	setTradeMatchIdRequiredState: function(e) {
		const { value } = e.target;

		if (value.toLowerCase() === 'xbrn') {
			this.tradeMatchIdField.classList.add(REQUIRED_FIELD_CLASS);
			this.tradeMatchIdInput.required = true;
		} else {
			this.tradeMatchIdField.classList.remove(REQUIRED_FIELD_CLASS);
			this.tradeMatchIdInput.required = false;
		}
	},

	updateCfiRequiredFields: function(cfiValue) {
		if (!this.cfiDefinition) {
			return;
		}

		const requiredFields = this.findRequiredFields(cfiValue);

		if (!requiredFields) {
			return;
		}

		this.clearPreviousCfiRequiredFields();

		requiredFields.required.forEach((fieldId) => {
			this.setFieldRequired(fieldId);
		});

		this.oldRequiredFields = requiredFields.required;
	},

	clearPreviousCfiRequiredFields() {
		if (!this.oldRequiredFields) {
			return;
		}

		this.oldRequiredFields.forEach((fieldId) => {
			this.removeFieldRequired(fieldId);
		});
	},

	setFieldRequired(fieldId)	{
		const field = this.query(this.el, `#${fieldId}`);

		if (!field) {
			return;
		}

		field.closest('.field').classList.add(REQUIRED_FIELD_CLASS);
		field.setAttribute('required', 'true');
	},

	removeFieldRequired(fieldId)	{
		const field = this.query(this.el, `#${fieldId}`);

		if (!field) {
			return;
		}

		field.closest('.field').classList.remove(REQUIRED_FIELD_CLASS);
		field.removeAttribute('required');
	},

	findRequiredFields: function(value) {
		return this.cfiDefinition.find(item =>  new RegExp(item.pattern, 'i').test(value));
	},

	setUnderlyingVisibility: function(value) {
		this.clearPreviousCfiRequiredFields();

		this.underlyingsSection.classList.toggle(IS_HIDDEN_CLASS, !value);
	},

	onContraFirmChange: function(e) {
		this.setContraFirmValidationRules();
	},

	addContraFirmIdSuggestView() {
		return this.subviews.contraFirmIdSuggestView = new Suggest.Views.Main({
			el: '.js-contra-firm-party-id',
			dataUrl: '/extranet/autocompleteactiveparticipant'
		});
	},

	setContraFirmValidationRules() {
		const value = this.contraFirmSelect.value.toLowerCase();

		this.cfiPartyIdSearchInput.value = '';
		
		switch(value) {
			case 'memb':
				this.cfiPartyIdSearchInput.removeAttribute('pattern');
				this.cfiPartyIdSearchInput.removeAttribute('maxlength');

				this.addContraFirmIdSuggestView().render();
				
				break;
			case 'lei':
				this.cfiPartyIdSearchInput.setAttribute('pattern', '^[A-Za-z0-9]{20}$');
				this.cfiPartyIdSearchInput.setAttribute('maxlength', '20');

				if (this.subviews.contraFirmIdSuggestView) {
					this.subviews.contraFirmIdSuggestView.destroy();
				}

				break;			
			default:
			case 'bic':
			case 'nid':
			case 'oth':
				this.cfiPartyIdSearchInput.removeAttribute('pattern');
				this.cfiPartyIdSearchInput.setAttribute('maxlength', '35');

				if (this.subviews.contraFirmIdSuggestView) {
					this.subviews.contraFirmIdSuggestView.destroy();
				}

				break;
		}
	},

	render: function() {
		// render subviews
		_.each(this.subviews, function(subview) {
			subview.render();
		});

		return this;
	}
});
